import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";

const API_BASE_URL = "/identity-service/api/v1/roles";

export default function useRoles() {
  const authToken = sessionStorage.getItem("authToken");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch roles
  const fetchAllRoles = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);

    try {
      const res = await apiClient.get(API_BASE_URL);

      setRoles(res.data);
    } catch (error) {
      handleAxiosErrors(error, "fetch all roles");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // fetch role by id
  const fetchRoleById = useCallback(
    async (roleId) => {
      if (!authToken) return;
      setLoading(true);

      try {
        const res = await apiClient.get(`${API_BASE_URL}/${roleId}`);

        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch role by id");
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // add role
  const addRole = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(API_BASE_URL, payload);

        setRoles((prev) => [res.data, ...prev]);
        toast.success("Role created successfully");
      } catch (error) {
        handleAxiosErrors(error, "add role");
      }
    },
    [authToken]
  );

  // update role
  const updateRole = useCallback(
    async (payload, roleId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.put(`${API_BASE_URL}/${roleId}`, payload);

        setRoles((prev) =>
          prev.map((role) => (role.id === roleId ? res.data : role))
        );
        toast.success("Role updated successfully");
      } catch (error) {
        handleAxiosErrors(error, "add role");
      }
    },
    [authToken]
  );

  // delete role
  const deleteRoleById = useCallback(
    async (roleId) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${API_BASE_URL}/${roleId}`);

        setRoles((prev) => prev.filter((role) => role.id !== roleId));
        toast.success("Role deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete role");
      }
    },
    [authToken]
  );

  // assign privileges to role
  const assignPrivilegesToRole = useCallback(
    async (roleId, payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.put(
          `${API_BASE_URL}/${roleId}/privileges`,
          payload
        );

        // setRoles((prev) => prev.filter((role) => role.id !== roleId));
        toast.success("Privileges assigned successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "assign privileges");
      }
    },
    [authToken]
  );

  // assign roles to user
  const assignRolesToUser = useCallback(
    async (userId, payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.put(
          `${API_BASE_URL}/user/${userId}/assign`,
          payload
        );

        toast.success("Roles assigned successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "assign roles to user");
      }
    },
    [authToken]
  );

  // fetch role privileges
  const fetchRolePrivileges = useCallback(
    async (roleId) => {
      // Read token fresh from sessionStorage to avoid stale closures
      const currentToken = sessionStorage.getItem("authToken");
      if (!currentToken) return;
      try {
        const data = await apiClient.get(
          `${API_BASE_URL}/${roleId}/privileges`
        );
        return data;
      } catch (error) {
        handleAxiosErrors(error, "fetch role privileges");
      }
    },
    [] // Remove authToken dependency, read fresh each time
  );

  // fetch user's assigned roles
  const fetchUserRoles = useCallback(
    async (userId) => {
      const currentToken = sessionStorage.getItem("authToken");
      if (!currentToken) return;
      try {
        const res = await apiClient.get(`${API_BASE_URL}/user/${userId}`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch user roles");
      }
    },
    [] // Remove authToken dependency, read fresh each time
  );

  // remove roles from user
  const removeRolesFromUser = useCallback(
    async (userId, roleIds = []) => {
      if (!authToken || !userId || roleIds.length === 0) return;
      try {
        await Promise.all(
          roleIds.map((roleId) =>
            apiClient.delete(`${API_BASE_URL}`, {
              params: { userId, roleId },
            })
          )
        );

        toast.success("Roles removed successfully");
      } catch (error) {
        handleAxiosErrors(error, "remove roles from user");
      }
    },
    [authToken]
  );

  // remove privileges from role
  const removePrivilegesFromRole = useCallback(
    async (roleId, privilegeIds = []) => {
      if (!authToken || !roleId || privilegeIds.length === 0) return;
      try {
        await Promise.all(
          privilegeIds.map((privId) =>
            apiClient.delete(`${API_BASE_URL}/${roleId}/privileges/${privId}`)
          )
        );

        toast.success("Privileges removed successfully");
      } catch (error) {
        handleAxiosErrors(error, "remove privileges");
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchAllRoles();
  }, [authToken, fetchAllRoles]);

  return {
    roles,
    loading,
    fetchRoleById,
    addRole,
    updateRole,
    deleteRoleById,
    assignPrivilegesToRole,
    assignRolesToUser,
    fetchUserRoles,
    removeRolesFromUser,
    removePrivilegesFromRole,
    fetchRolePrivileges,
  };
}
